import { useEffect, useRef, useState } from 'react';

import { type StudioTeamOrg, adminClient } from '@/api/adminClient';

import { pickOrg, readRememberedOrg, rememberOrg } from '../Users/usersModel';

/** The organization of the page: the one remembered from the Users page, else the biggest (the rule of the Users page). */
export function useOrgChoice() {
  const [orgs, setOrgs] = useState<StudioTeamOrg[]>([]);
  const [chosen, setChosen] = useState<string | undefined>(() => readRememberedOrg());
  const [error, setError] = useState<string>();
  useEffect(() => {
    adminClient
      .getTeamAccess()
      .then((access) => setOrgs(access.orgs ?? []))
      .catch((failure) => setError(failure instanceof Error ? failure.message : 'Could not load the organizations.'));
  }, []);
  return {
    orgs,
    orgId: pickOrg(orgs, chosen),
    error,
    choose: (next: string) => {
      rememberOrg(next);
      setChosen(next);
    },
  };
}

/** The app id without its environment: `app-<hex>.published` is `app-<hex>`. */
export const bareApp = (app: string) => app.replace(/\.\w+$/, '');

/** The title of an app by its id, in the organization the page shows. An app with no title reads "An app". */
export function useAppNames(ids: string[]): (app: string) => string {
  const { orgId } = useOrgChoice();
  const [names, setNames] = useState<{ orgId?: string; byId: Record<string, string> }>({ byId: {} });
  const asked = useRef({ orgId: undefined as string | undefined, ids: new Set<string>() });
  const key = [...new Set(ids.filter(Boolean))].sort().join(' ');
  useEffect(() => {
    if (!orgId) return;
    if (asked.current.orgId !== orgId) asked.current = { orgId, ids: new Set() };
    const fresh = key.split(' ').filter((id) => id && !asked.current.ids.has(id));
    fresh.forEach((id) => asked.current.ids.add(id));
    if (!fresh.length) return;
    adminClient
      .getPermissionAppLabels(orgId, fresh)
      .then((named) => setNames((old) => ({ orgId, byId: { ...(old.orgId === orgId ? old.byId : {}), ...named } })))
      .catch(() => undefined); // no titles: the page shows "An app"
  }, [key, orgId]);
  const byId = names.orgId === orgId ? names.byId : {};
  return (app) => (app ? (byId[app] ?? byId[bareApp(app)] ?? 'An app') : '–');
}

/**
 * The email of a person by service person id, in the organization the page shows. A person the roster does not know reads
 * "Unknown person", never the id. When the names cannot load, every name reads "Names could not load".
 */
export const usePersonNames = () => usePersonNamesOf(useOrgChoice().orgId);

export function usePersonNamesOf(orgId: string | undefined): (person: string) => string {
  const [state, setState] = useState<{ orgId?: string; people?: Record<string, string>; failed?: boolean }>({});
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false; // a late answer for an old organization must not replace a newer one
    adminClient
      .getPermissionPeople(orgId)
      .then((out) => !cancelled && setState({ orgId, people: out.people }))
      .catch(() => !cancelled && setState({ orgId, failed: true }));
    return () => {
      cancelled = true;
    };
  }, [orgId]);
  const mine = state.orgId === orgId ? state : {};
  return (person) => (mine.failed ? 'Names could not load' : (mine.people?.[person.replace(/^user:/, '')] ?? 'Unknown person'));
}
